import type { IServer } from '@web/entities/server/model/server.type'

type TStoredServer = IServer | Omit<IServer, 'url'>

const createServerUrl = (
  server: Pick<IServer, 'host' | 'isSecure' | 'port'>
): string => {
  const protocol = server.isSecure ? 'https://' : 'http://'

  return `${protocol}${server.host}:${server.port}`
}

const normalizeServer = (server: TStoredServer): IServer => {
  const url = 'url' in server ? server.url : createServerUrl(server)

  return {
    ...server,
    id: server.id || url,
    url
  }
}

class ServerStorage {
  private readonly SERVERS_KEY = 'servers'
  private readonly ACTIVE_SERVER_KEY = 'active_server'

  public getServers(): IServer[] {
    try {
      const raw = localStorage.getItem(this.SERVERS_KEY)
      return raw
        ? (JSON.parse(raw) as TStoredServer[]).map(normalizeServer)
        : []
    } catch (error) {
      console.error('Ошибка при чтении списка серверов:', error)
      return []
    }
  }

  public saveServers(servers: IServer[]): void {
    try {
      localStorage.setItem(this.SERVERS_KEY, JSON.stringify(servers))
    } catch (error) {
      console.error('Ошибка при сохранении серверов:', error)
    }
  }

  public addOrUpdate(server: IServer): void {
    const servers = this.getServers()

    // Проверка на дубликаты по host + port + isSecure
    const duplicate = servers.find(
      (s) => s.url === server.url && s.id !== server.id
    )

    if (duplicate) {
      console.warn('Сервер с такими параметрами уже существует:', duplicate)
      return
    }

    const index = servers.findIndex((s) => s.id === server.id)
    if (index !== -1) {
      servers[index] = { ...servers[index], ...server }
    } else {
      servers.push(server)
    }

    this.saveServers(servers)
  }

  public remove(id: string): void {
    const updated = this.getServers().filter((s) => s.id !== id)
    this.saveServers(updated)

    const activeId = this.getActiveServerId()
    if (activeId && activeId === id) {
      this.clearActive()
    }
  }

  public setActiveServerId(serverId: string): void {
    try {
      localStorage.setItem(this.ACTIVE_SERVER_KEY, serverId)
    } catch (error) {
      console.error('Ошибка при установке активного сервера:', error)
    }
  }

  public getActiveServerId(): string | null {
    try {
      const raw = localStorage.getItem(this.ACTIVE_SERVER_KEY)
      return raw
    } catch (error) {
      console.error('Ошибка при чтении активного сервера:', error)
      return null
    }
  }

  public getActiveServerConfig(): IServer | null {
    const activeId = this.getActiveServerId()
    if (!activeId) return null
    return this.getServers().find((s) => s.id === activeId) ?? null
  }

  public clearActive(): void {
    localStorage.removeItem(this.ACTIVE_SERVER_KEY)
  }
}

export const serverStorage = new ServerStorage()
